import React from 'react';
import { useAllPoolsData } from '../hooks/useAllPoolsData';
import { POOL_CONFIGS } from '../config/pools';
import { StackedLiquidityChart } from './charts/StackedLiquidityChart';
import { TVLPieChart } from './charts/TVLPieChart';
import { TVLByDEXChart } from './charts/TVLByDEXChart';
import { cn } from '../utils/cn';
import './charts/ChartStyles.css';

interface DashboardProps {
  className?: string;
}

export const Dashboard: React.FC<DashboardProps> = ({ className }) => {
  // Use the custom hook that handles all pools
  const poolsData = useAllPoolsData(1000); // Fetch maximum available data to match Python's full historical data
  
  // Add debugging
  console.log('Dashboard rendered, poolsData:', poolsData);
  
  // Debug: Log specific pool TVL values for comparison
  React.useEffect(() => {
    Object.entries(poolsData).forEach(([poolKey, poolData]) => {
      if (poolData.snapshots && poolData.snapshots.length > 0) {
        const latestSnapshot = poolData.snapshots[poolData.snapshots.length - 1];
        console.log(`Pool ${poolKey}: Latest TVL = $${latestSnapshot.total_value_locked_usd}`);
      }
    });
  }, [poolsData]);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    const totalPools = Object.keys(poolsData).length;
    // Active pools are those with snapshots AND TVL > 0
    const poolsWithData = Object.values(poolsData).filter(pool => {
      if (pool.snapshots && pool.snapshots.length > 0) {
        const latestSnapshot = pool.snapshots[pool.snapshots.length - 1];
        const tvl = latestSnapshot.total_value_locked_usd || 0;
        return tvl > 0;
      }
      return false;
    }).length;
    
    const totalTVL = Object.values(poolsData).reduce((sum, pool) => {
      if (pool.snapshots && pool.snapshots.length > 0) {
        const latestSnapshot = pool.snapshots[pool.snapshots.length - 1];
        return sum + (latestSnapshot.total_value_locked_usd || 0);
      }
      return sum;
    }, 0);

    const dexCounts = Object.values(poolsData).reduce((counts, pool) => {
      if (pool.snapshots && pool.snapshots.length > 0) {
        const latestSnapshot = pool.snapshots[pool.snapshots.length - 1];
        const tvl = latestSnapshot.total_value_locked_usd || 0;
        // Only count pools with TVL > 0
        if (tvl > 0) {
          const poolConfig = POOL_CONFIGS.find(p => 
            `${p.dex}_${p.address}` === Object.keys(poolsData).find(key => poolsData[key] === pool)
          );
          if (poolConfig) {
            counts[poolConfig.dex] = (counts[poolConfig.dex] || 0) + 1;
          }
        }
      }
      return counts;
    }, {} as { [dex: string]: number });

    return {
      totalPools,
      poolsWithData,
      totalTVL,
      dexCounts
    };
  }, [poolsData]);

  // Check if any pools are loading
  const isLoading = Object.values(poolsData).some(pool => pool.loading);
  const hasError = Object.values(poolsData).some(pool => pool.error);

  // Sort pools by TVL (descending order) for the table
  const sortedPools = React.useMemo(() => {
    return POOL_CONFIGS.map((poolConfig) => {
      const poolKey = `${poolConfig.dex}_${poolConfig.address}`;
      const poolData = poolsData[poolKey];
      const latestTVL = poolData?.snapshots?.[poolData.snapshots.length - 1]?.total_value_locked_usd || 0;
      return { poolConfig, poolKey, poolData, latestTVL };
    }).sort((a, b) => b.latestTVL - a.latestTVL);
  }, [poolsData]);

  return (
    <div className={cn('dashboard', className)}>
      <div className="dashboard-header ev-brand-header">
        <div className="ev-background-lines"></div>
        <h1 className="ev-heading-primary">EverValue Liquidity Dashboard</h1>
        <p>Real-time liquidity analysis across multiple DEXes</p>
        <p className="ev-refresh-indicator">🔄 Auto-refreshing every 1 hour</p>
        {isLoading && <p className="ev-loading-pulse">🔄 Loading pool data<span className="loading-dots"></span></p>}
        {hasError && <p>⚠️ Some pools may have loading errors</p>}
      </div>

      {/* Summary Statistics */}
      <div className="summary-stats">
        <div className="stat-card ev-card hover-lift animate-fade-in-up animate-stagger-1">
          <h3>Total Pools</h3>
          <p className="stat-value ev-metric-value">{summaryStats.totalPools}</p>
        </div>
        <div className="stat-card ev-card hover-lift animate-fade-in-up animate-stagger-2">
          <h3>Active Pools</h3>
          <p className="stat-value ev-metric-value">{summaryStats.poolsWithData}</p>
        </div>
        <div className="stat-card ev-card hover-lift animate-fade-in-up animate-stagger-3">
          <h3>Total TVL</h3>
          <p className="stat-value ev-metric-value" title={`$${summaryStats.totalTVL.toLocaleString(undefined, {maximumFractionDigits: 2})}`}>
            {summaryStats.totalTVL >= 1000000000 
              ? `$${(summaryStats.totalTVL / 1000000000).toFixed(1)}B`
              : summaryStats.totalTVL >= 1000000 
              ? `$${(summaryStats.totalTVL / 1000000).toFixed(1)}M`
              : summaryStats.totalTVL >= 1000 
              ? `$${(summaryStats.totalTVL / 1000).toFixed(1)}K`
              : `$${summaryStats.totalTVL.toLocaleString(undefined, {maximumFractionDigits: 2})}`
            }
          </p>
        </div>
        <div className="stat-card ev-card hover-lift animate-fade-in-up animate-stagger-4">
          <h3>DEXes</h3>
          <p className="stat-value ev-metric-value">{Object.keys(summaryStats.dexCounts).length}</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="dashboard-grid">
        {/* Stacked Liquidity Chart - Full Width */}
        <div className="chart-full-width animate-fade-in-up animate-stagger-1">
          <StackedLiquidityChart poolsData={poolsData} />
        </div>

        {/* TVL Distribution Charts - Side by Side */}
        <div className="animate-fade-in-up animate-stagger-2">
          <TVLPieChart poolsData={poolsData} />
        </div>
        <div className="animate-fade-in-up animate-stagger-3">
          <TVLByDEXChart poolsData={poolsData} />
        </div>
      </div>

      {/* Pool Details Table */}
      <div className="pools-table-container animate-fade-in-up animate-stagger-4">
        <h2 className="chart-title ev-accent separator-wide">Pool Details</h2>
        <div className="pools-table">
          <table>
            <thead>
              <tr>
                <th>Pool Name</th>
                <th>DEX</th>
                <th>Current TVL</th>
                <th>Status</th>
                <th>Scan</th>
                <th>Pool</th>
              </tr>
            </thead>
            <tbody>
              {sortedPools.map(({ poolConfig, poolKey, poolData, latestTVL }) => {
                const isLoading = poolData?.loading || false;
                const hasError = poolData?.error || false;

                return (
                  <tr key={poolKey}>
                    <td>{poolConfig.name}</td>
                    <td>{poolConfig.dex.replace('_', ' ').toUpperCase()}</td>
                    <td>${latestTVL.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                    <td>
                      {isLoading ? <span className="ev-status-loading">🔄 Loading...</span> : 
                       hasError ? <span className="ev-status-error">❌ Error</span> : 
                       latestTVL > 0 ? <span className="ev-status-active">✅ Active</span> : <span className="ev-status-inactive">⚠️ No Data</span>}
                    </td>
                    <td>
                      {poolConfig.scanUrl ? (
                        <a 
                          href={poolConfig.scanUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="scan-link-btn"
                          title={`View ${poolConfig.name} on blockchain explorer`}
                        >
                          🔍 Scan
                        </a>
                      ) : (
                        <span className="no-link">-</span>
                      )}
                    </td>
                    <td>
                      {poolConfig.poolUrl ? (
                        <a 
                          href={poolConfig.poolUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pool-link-btn"
                          title={`View ${poolConfig.name} on ${poolConfig.dex.replace('_', ' ').toUpperCase()}`}
                        >
                          View Pool
                        </a>
                      ) : (
                        <span className="no-link">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
